import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import styled from 'styled-components';

import { FormCard } from '../../../../components';
import {
  AddressAttachmentUploadDialog,
  INEAttachmentUploadDialog,
  TaxAttachmentUploadDialog
} from '../../../../shared/dialogConfigs';
import { ATTACHMENT_FIELDS } from '../../../../shared/fieldConfig/constants';
import { CONTROL_TYPE } from '../../../../utils/constants';
import {
  getUploadRequiredValidation,
  handleRemoveFile,
  onRemoveFile,
  openUploadDialog
} from '../../../../utils/fileUploadHelpers';
import AttachmentUpload from '../../../AttachmentUpload';
import type {
  CardDispatch,
  CardErrorMustContain,
  CardField,
  CardFieldComponent,
  CardFieldProps,
  CardStore
} from '../../types';

const Wrapper = styled.div`
  width: 100%;
`;

const category = 'financial.attachments';

interface AttachmentCardProps {
  isReview?: boolean;
  store: CardStore;
  fields: CardField[];
  errorMustContain?: CardErrorMustContain;
  cardTitle?: string;
  cardSubTitle?: string;
}

function AddressAttachment({ showUpload, t, dispatch, apiService, store, isReview }: CardFieldProps) {
  const field = 'addressAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    label: t('address.taxExempt.attachments.label'),
    uploadButtonLabel: t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: AddressAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return (
    <Box sx={{ marginBottom: 'var(--spacing-base)' }}>
      <AttachmentUpload additionalDocument={true} isReview={isReview} {...attachmentsConfig} />
    </Box>
  );
}

function TaxAttachment({ showUpload, t, dispatch, apiService, store, isReview }: CardFieldProps) {
  const field = 'taxAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    label: t('address.taxExempt.attachments.label'),
    uploadButtonLabel: t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: TaxAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return (
    <Box sx={{ marginBottom: 'var(--spacing-base)' }}>
      <AttachmentUpload additionalDocument={true} isReview={isReview} {...attachmentsConfig} />
    </Box>
  );
}

function INEAttachment({ showUpload, t, dispatch, apiService, store, isReview }: CardFieldProps) {
  const field = 'ineAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    label: t('address.taxExempt.attachments.label'),
    uploadButtonLabel: t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: INEAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return (
    <Box sx={{ marginBottom: 'var(--spacing-base)' }}>
      <AttachmentUpload additionalDocument={true} isReview={isReview} {...attachmentsConfig} />
    </Box>
  );
}

function ConstitutiveActAttachment({ showUpload, t, dispatch, apiService, store, isReview }: CardFieldProps) {
  const field = 'constitutiveActAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    label: t('address.taxExempt.attachments.label'),
    uploadButtonLabel: t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: INEAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return (
    <Box sx={{ marginBottom: 'var(--spacing-base)' }}>
      <AttachmentUpload additionalDocument={true} isReview={isReview} {...attachmentsConfig} />
    </Box>
  );
}

function FinancialStatementsAttachment({
  showUpload,
  t,
  dispatch,
  apiService,
  store,
  hideUploadButtonLabel,
  isReview
}: CardFieldProps) {
  const field = 'financialStatementsAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    label: t('address.taxExempt.attachments.label'),
    uploadButtonLabel: hideUploadButtonLabel ? null : t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: INEAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return (
    <Box sx={{ marginBottom: 'var(--spacing-base)' }}>
      <AttachmentUpload additionalDocument={true} isReview={isReview} {...attachmentsConfig} />
    </Box>
  );
}

function AdditionalAttachment({ showUpload, t, dispatch, apiService, store, isReview }: CardFieldProps) {
  const field = 'additionalAttachment';
  const attachments = store.attachments.uploads[field].files;
  const required = getUploadRequiredValidation(store, field);
  const attachmentsConfig = {
    store,
    category,
    field,
    isAdditionalAttachment: true,
    label: t('address.taxExempt.attachments.label'),
    // uploadButtonLabel: t(`${category}.${field}.label`),
    type: CONTROL_TYPE.attachmentList,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService, () =>
      onRemoveFile(field, required, attachments, dispatch as CardDispatch, t)
    ),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: AddressAttachmentUploadDialog as any,
      field
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: showUpload,
    required,
    dispatch: dispatch as CardDispatch,
    listRight: true,
    attachments
  };

  return <AttachmentUpload isReview={isReview} additionalDocument={true} {...attachmentsConfig} />;
}
const controlMap: Record<string, CardFieldComponent> = {
  [ATTACHMENT_FIELDS.taxAttachment]: TaxAttachment,
  [ATTACHMENT_FIELDS.ineAttachment]: INEAttachment,
  [ATTACHMENT_FIELDS.addressAttachment]: AddressAttachment,
  [ATTACHMENT_FIELDS.financialStatementsAttachment]: FinancialStatementsAttachment,
  [ATTACHMENT_FIELDS.constitutiveActAttachment]: ConstitutiveActAttachment,
  [ATTACHMENT_FIELDS.additionalAttachment]: AdditionalAttachment
};

export function AdditionalAttachmentCard(props: AttachmentCardProps) {
  const { isReview, store, fields, errorMustContain, cardTitle, cardSubTitle } = props;
  const { t } = useTranslation();
  const cardId = 'additionalAttachmentCard';
  const showUpload = !isReview || store.editMode.attachmentCard;
  const cardProps = {
    cardId,
    title: t(`${cardTitle || 'financial.additionalAttachmentCard.title'}`),
    subtitle: t(`${cardSubTitle || 'financial.additionalAttachmentCard.subtitle'}`),
    image: '/food/onboarding/assets/Attachments.svg',
    isReview,
    store,
    errorMustContain
  };
  return (
    <FormCard {...cardProps}>
      <Wrapper>
        {fields.map(({ key, ...fieldProps }: CardField) => {
          const Component = controlMap[key];
          return <Component t={t} showUpload={showUpload} isReview={isReview} {...fieldProps} {...props} key={key} />;
        })}
      </Wrapper>
    </FormCard>
  );
}

function AttachmentCard(props: AttachmentCardProps) {
  const { isReview, store, fields, errorMustContain, cardTitle, cardSubTitle } = props;
  const { t } = useTranslation();
  const cardId = 'attachmentCard';
  const showUpload = !isReview || store.editMode.attachmentCard;
  const cardProps = {
    cardId,
    title: t(`${cardTitle || 'financial.attachments.title'}`),
    subtitle: t(`${cardSubTitle || 'financial.attachments.subtitle'}`),
    image: '/food/onboarding/assets/Attachments.svg',
    isReview,
    store,
    errorMustContain
  };
  return (
    <FormCard {...cardProps}>
      <Wrapper>
        {fields.map(({ key, ...fieldProps }: CardField) => {
          const Component = controlMap[key];
          return <Component t={t} showUpload={showUpload} isReview={isReview} {...fieldProps} {...props} key={key} />;
        })}
      </Wrapper>
    </FormCard>
  );
}

export default AttachmentCard;
